import {useState, useEffect, useCallback, useRef} from 'react';
import {useNetworkStatus} from './useNetworkStatus';
import {NetworkError} from '../services/api';
import {theme} from '../components/DarkTheme';

export function useDataLoader(loadFn: () => Promise<void>) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<'network' | 'other' | null>(null);
  const isOnline = useNetworkStatus();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;

  const execute = useCallback(async () => {
    try {
      setLoadError(null);
      await loadFnRef.current();
    } catch (error) {
      if (error instanceof NetworkError) {
        setLoadError('network');
      } else {
        setLoadError('other');
      }
      console.error('Data load failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    execute();
  }, [execute]);

  // Auto-retry when connectivity is restored after a network error
  useEffect(() => {
    if (isOnline && loadError === 'network') {
      execute();
    }
  }, [isOnline, loadError, execute]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await execute();
    setIsRefreshing(false);
  }, [execute]);

  const refreshControlProps = {
    refreshing: isRefreshing,
    onRefresh: handleRefresh,
    tintColor: theme.primary,
    colors: [theme.primary],
  };

  return {
    isLoading,
    loadError,
    isOnline,
    refresh: execute,
    refreshControlProps,
  };
}
